// PanelSizingAndFrame.js
// Sizing & Frame section for Steel Doors.
// Owns its controlMap; bubbles up the full section data via onChange(sectionData).
// All show/hide dependencies are driven by React state — no DOM manipulation.

import React, { useState, useEffect, useRef } from 'react';
import ExpandableSection from '../../../expandablesection';

import DoorSize           from '../../controls/frame/DoorSize';
import DoorHeight         from '../../controls/frame/DoorHeight';
import FrameWidth         from '../../controls/frame/FrameWidth';
import FrameHeight        from '../../controls/frame/FrameHeight';
import Jambs              from '../../controls/frame/Jambs';
import JambMaterial       from '../../controls/frame/JambMaterial';
import FrameExtension     from '../../controls/frame/FrameExtension';
import OverrideExtension  from '../../controls/frame/OverrideExtension';
import Swing              from '../../controls/frame/Swing';
import Hinges             from '../../controls/frame/Hinges';
import HingeFinish        from '../../controls/frame/HingeFinish';
import DecorativeTips     from '../../controls/frame/DecorativeTips';
import OperatingSideLite  from '../../controls/frame/OperatingSideLite';
import MultipointPrep     from '../../controls/frame/MultipointPrep';
import DeadboltHole       from '../../controls/frame/DeadboltHole';
import PassagePrep        from '../../controls/frame/PassagePrep';
import WeatherStrip       from '../../controls/frame/WeatherStrip';
import WeatherStripColor  from '../../controls/frame/WeatherStripColor';
import Flex               from '../../controls/frame/Flex';
import EnergyStarGlass    from '../../controls/frame/EnergyStarGlass';
import SecurityWindowFilm from '../../controls/frame/SecurityWindowFilm';
import LaminatedGlass     from '../../controls/frame/LaminatedGlass';

const ev = { value: '', label: '' }; // empty value shorthand

const PanelSizingAndFrame = ({
  data         = {},
  onChange,
  selectedDoor = null,
  disabled     = false,
  sectionId    = 'SizingAndFrame',
  state,
  onExpandChange,
  forceExpanded,
}) => {

  // ── section state ─────────────────────────────────────────────────────────
  const [doorSize,          setDoorSize]          = useState(data.doorSize          || ev);
  const [doorHeight,        setDoorHeight]        = useState(data.doorHeight?.value ? data.doorHeight : { value: "6'8\"", label: "6'8\"" });
  const [frameWidth,        setFrameWidth]        = useState(data.frameWidth        || ev);
  const [frameHeight,       setFrameHeight]       = useState(data.frameHeight       || ev);
  const [jambs,             setJambs]             = useState(data.jambs             || ev);
  const [jambMaterial,      setJambMaterial]      = useState(data.jambMaterial      || ev);
  const [frameExtension,    setFrameExtension]    = useState(data.frameExtension    || ev);
  const [overrideExtension, setOverrideExtension] = useState(data.overrideExtension || ev);
  const [swing,             setSwing]             = useState(data.swing             || ev);
  const [hinges,            setHinges]            = useState(data.hinges            || ev);
  const [hingeFinish,       setHingeFinish]       = useState(data.hingeFinish       || ev);
  const [decorativeTips,    setDecorativeTips]    = useState(data.decorativeTips    || ev);
  const [operatingSideLite, setOperatingSideLite] = useState(data.operatingSideLite || ev);
  const [multipointPrep,    setMultipointPrep]    = useState(data.multipointPrep    || ev);
  const [deadboltHole,      setDeadboltHole]      = useState(data.deadboltHole      || ev);
  const [passagePrep,       setPassagePrep]       = useState(data.passagePrep       || ev);
  const [weatherStrip,      setWeatherStrip]      = useState(data.weatherStrip      || ev);
  const [weatherStripColor, setWeatherStripColor] = useState(data.weatherStripColor || ev);
  const [flex,              setFlex]              = useState(data.flex              || ev);
  const [energyStarGlass,   setEnergyStarGlass]   = useState(data.energyStarGlass   || ev);
  const [securityFilm,      setSecurityFilm]      = useState(data.securityFilm      || ev);
  const [laminatedGlass,    setLaminatedGlass]    = useState(data.laminatedGlass    || ev);

  // ── conditional visibility flags ─────────────────────────────────────────
  const [showOverrideExtension, setShowOverrideExtension] = useState(false);
  const [showDecorativeTips,    setShowDecorativeTips]    = useState(false);
  const [showOperatingSideLite, setShowOperatingSideLite] = useState(false);
  const [showDeadboltPassage,   setShowDeadboltPassage]   = useState(false);

  // ── sync when parent loads saved data (once, when data arrives non-empty) ─
  const loadedRef = useRef(false);
  useEffect(() => {
    if (loadedRef.current) return;           // only run once per data load
    if (!data || Object.keys(data).length === 0) return;
    loadedRef.current = true;
    setDoorSize(data.doorSize                   || ev);
    setDoorHeight(data.doorHeight?.value ? data.doorHeight : { value: "6'8\"", label: "6'8\"" });
    setFrameWidth(data.frameWidth               || ev);
    setFrameHeight(data.frameHeight             || ev);
    setJambs(data.jambs                         || ev);
    setJambMaterial(data.jambMaterial           || ev);
    setFrameExtension(data.frameExtension       || ev);
    setOverrideExtension(data.overrideExtension || ev);
    setSwing(data.swing                         || ev);
    setHinges(data.hinges                       || ev);
    setHingeFinish(data.hingeFinish             || ev);
    setDecorativeTips(data.decorativeTips       || ev);
    setOperatingSideLite(data.operatingSideLite || ev);
    setMultipointPrep(data.multipointPrep       || ev);
    setDeadboltHole(data.deadboltHole           || ev);
    setPassagePrep(data.passagePrep             || ev);
    setWeatherStrip(data.weatherStrip           || ev);
    setWeatherStripColor(data.weatherStripColor || ev);
    setFlex(data.flex                           || ev);
    setEnergyStarGlass(data.energyStarGlass     || ev);
    setSecurityFilm(data.securityFilm           || ev);
    setLaminatedGlass(data.laminatedGlass       || ev);
  }, [data]);

  // ── bubble up — gated by a ref so it never triggers its own re-run ────────
  const isMounted = useRef(false);
  useEffect(() => {
    if (!isMounted.current) { isMounted.current = true; return; } // skip first mount
    onChange?.({
      doorSize,        doorHeight,
      frameWidth,      frameHeight,
      jambs,           jambMaterial,
      frameExtension,  overrideExtension,
      swing,           hinges,      hingeFinish, decorativeTips, operatingSideLite,
      multipointPrep,  deadboltHole, passagePrep,
      weatherStrip,    weatherStripColor, flex,
      energyStarGlass, securityFilm,      laminatedGlass,
    });
  }, [
    doorSize, doorHeight, frameWidth, frameHeight,
    jambs, jambMaterial, frameExtension, overrideExtension,
    swing, hinges, hingeFinish, decorativeTips, operatingSideLite,
    multipointPrep, deadboltHole, passagePrep,
    weatherStrip, weatherStripColor, flex,
    energyStarGlass, securityFilm, laminatedGlass,
  ]);

  // ── section title ─────────────────────────────────────────────────────────
  const title = doorSize?.value
    ? `📐 Sizing & Frame  (${doorSize.value} × ${doorHeight.value || '?'})`
    : '📐 Sizing & Frame';

  // ── controlMap ────────────────────────────────────────────────────────────
  const controlMap = [
    {
      id: 'DoorSize',
      component: <DoorSize value={doorSize} onChange={setDoorSize} disabled={disabled} />,
      position: [2, 1],
      status: 'active',
      expandableSectionId: sectionId,
    },
    {
      id: 'DoorHeight',
      component: <DoorHeight value={doorHeight} onChange={setDoorHeight} disabled={disabled} />,
      position: [2, 2],
      status: 'active',
      expandableSectionId: sectionId,
    },
    {
      id: 'FrameWidth',
      component: (
        <FrameWidth
          value={frameWidth}
          onChange={setFrameWidth}
          disabled={disabled}
          doorSize={doorSize}
          selectedDoor={selectedDoor}
        />
      ),
      position: [3, 1],
      status: 'active',
      expandableSectionId: sectionId,
    },
    {
      id: 'FrameHeight',
      component: (
        <FrameHeight
          value={frameHeight}
          onChange={setFrameHeight}
          disabled={disabled}
          doorHeight={doorHeight}
        />
      ),
      position: [3, 2],
      status: 'active',
      expandableSectionId: sectionId,
    },
    {
      id: 'Jambs',
      component: <Jambs value={jambs} onChange={setJambs} disabled={disabled} />,
      position: [4, 1],
      status: 'active',
      expandableSectionId: sectionId,
    },
    {
      id: 'JambMaterial',
      component: <JambMaterial value={jambMaterial} onChange={setJambMaterial} disabled={disabled} jambs={jambs} />,
      position: [4, 2],
      status: 'active',
      expandableSectionId: sectionId,
    },
    {
      id: 'FrameExtension',
      component: (
        <FrameExtension
          value={frameExtension}
          onChange={setFrameExtension}
          disabled={disabled}
          onOverrideVisible={setShowOverrideExtension}
        />
      ),
      position: [5, 1],
      status: 'active',
      expandableSectionId: sectionId,
    },
    // conditional: shown when FrameExtension is 1", 1 5/8", or 2"
    ...(showOverrideExtension ? [{
      id: 'OverrideExtension',
      component: (
        <OverrideExtension
          value={overrideExtension}
          onChange={setOverrideExtension}
          disabled={disabled}
          frameExtension={frameExtension}
        />
      ),
      position: [5, 2],
      status: 'active',
      expandableSectionId: sectionId,
    }] : []),
    {
      id: 'Swing',
      component: (
        <Swing
          value={swing}
          onChange={setSwing}
          disabled={disabled}
          onSwingChange={(v) => setShowDecorativeTips(v === 'Outswing')}
        />
      ),
      position: [6, 1],
      status: 'active',
      expandableSectionId: sectionId,
    },
    {
      id: 'Hinges',
      component: (
        <Hinges
          value={hinges}
          onChange={setHinges}
          disabled={disabled}
          selectedDoor={selectedDoor}
          onOperatingSideLiteVisible={setShowOperatingSideLite}
        />
      ),
      position: [6, 2],
      status: 'active',
      expandableSectionId: sectionId,
    },
    {
      id: 'HingeFinish',
      component: <HingeFinish value={hingeFinish} onChange={setHingeFinish} disabled={disabled} swing={swing} />,
      position: [6, 3],
      status: 'active',
      expandableSectionId: sectionId,
    },
    // conditional: shown when Swing = Outswing
    ...(showDecorativeTips ? [{
      id: 'DecorativeTips',
      component: <DecorativeTips value={decorativeTips} onChange={setDecorativeTips} disabled={disabled} />,
      position: [7, 1],
      status: 'active',
      expandableSectionId: sectionId,
    }] : []),
    // conditional: shown when hinges side matches a side lite on the selected door type
    ...(showOperatingSideLite ? [{
      id: 'OperatingSideLite',
      component: <OperatingSideLite value={operatingSideLite} onChange={setOperatingSideLite} disabled={disabled} />,
      position: [8, 1],
      status: 'active',
      expandableSectionId: sectionId,
    }] : []),
    {
      id: 'MultipointPrep',
      component: (
        <MultipointPrep
          value={multipointPrep}
          onChange={setMultipointPrep}
          disabled={disabled}
          onChildrenVisible={(v) => setShowDeadboltPassage(v)}
        />
      ),
      position: [9, 1],
      status: 'active',
      expandableSectionId: sectionId,
    },
    // conditional: shown when MultipointPrep = Yes
    ...(showDeadboltPassage ? [
      {
        id: 'DeadboltHole',
        component: (
          <DeadboltHole
            value={deadboltHole}
            onChange={setDeadboltHole}
            disabled={disabled}
            selectedDoor={selectedDoor}
          />
        ),
        position: [9, 2],
        status: 'active',
        expandableSectionId: sectionId,
      },
      {
        id: 'PassagePrep',
        component: (
          <PassagePrep
            value={passagePrep}
            onChange={setPassagePrep}
            disabled={disabled}
            selectedDoor={selectedDoor}
          />
        ),
        position: [9, 3],
        status: 'active',
        expandableSectionId: sectionId,
      },
    ] : []),
    {
      id: 'WeatherStrip',
      component: <WeatherStrip value={weatherStrip} onChange={setWeatherStrip} disabled={disabled} />,
      position: [10, 1],
      status: 'active',
      expandableSectionId: sectionId,
    },
    {
      id: 'WeatherStripColor',
      component: <WeatherStripColor value={weatherStripColor} onChange={setWeatherStripColor} disabled={disabled} />,
      position: [10, 2],
      status: 'active',
      expandableSectionId: sectionId,
    },
    {
      id: 'Flex',
      component: <Flex value={flex} onChange={setFlex} disabled={disabled} />,
      position: [10, 3],
      status: 'active',
      expandableSectionId: sectionId,
    },
    {
      id: 'EnergyStarGlass',
      component: <EnergyStarGlass value={energyStarGlass} onChange={setEnergyStarGlass} disabled={disabled} />,
      position: [11, 1],
      status: 'active',
      expandableSectionId: sectionId,
    },
    {
      id: 'SecurityWindowFilm',
      component: <SecurityWindowFilm value={securityFilm} onChange={setSecurityFilm} disabled={disabled} />,
      position: [11, 2],
      status: 'active',
      expandableSectionId: sectionId,
    },
    {
      id: 'LaminatedGlass',
      component: <LaminatedGlass value={laminatedGlass} onChange={setLaminatedGlass} disabled={disabled} />,
      position: [11, 3],
      status: 'active',
      expandableSectionId: sectionId,
    },
  ];

  return (
    <ExpandableSection
      title={title}
      sectionId={sectionId}
      state={state}
      onExpandChange={onExpandChange}
      forceExpanded={forceExpanded}
      panelLocation="SizingAndFrame"
      controlMap={controlMap.filter(c => c.expandableSectionId === sectionId)}
    />
  );
};

export default PanelSizingAndFrame;
